/**
 * Admin-domain mutations — per-domain mutation module for the admin screens
 * (hourly rates, clients, tags, designation catalogue, users, roles). All
 * writes go through the REST client (kronos-api).
 *
 * The server stamps createdAt/updatedAt so the client omits them; the
 * favorites/hidden toggles have no array-sentinel equivalent over REST, so
 * they do a read-modify-write instead (see setProjectFavourite).
 * See docs/superpowers/specs/2026-07-06-china-p2-client-seam-design.md.
 */
import { Timestamp } from 'firebase/firestore';
import { formatDisplayName } from '@altyor-kronos/shared-types';
import type { CatalogSection, BillingMode, Client } from '@altyor-kronos/shared-types';
import { apiGet, apiPost, apiPut, apiDelete } from './restClient';

// --- hourlyRates (HourlyRatesTab.tsx) ---
export async function createHourlyRate(input: { label: string; amount: number }): Promise<void> {
  const payload = { label: input.label, amount: input.amount, currency: 'EUR' };
  await apiPost('/hourlyRates', payload);
}
export async function updateHourlyRate(id: string, input: { label: string; amount: number }): Promise<void> {
  const patch = { label: input.label, amount: input.amount, currency: 'EUR' };
  await apiPut(`/hourlyRates/${id}`, patch);
}
export async function deleteHourlyRate(id: string): Promise<void> {
  await apiDelete(`/hourlyRates/${id}`);
}

// --- clients (ClientsTab.tsx) ---
export async function createClient(data: Record<string, unknown>): Promise<string> {
  const res = await apiPost<{ id: string }>('/clients', data);
  return res.id;
}
export async function updateClient(id: string, data: Record<string, unknown>): Promise<void> {
  await apiPut(`/clients/${id}`, data);
}

export interface EnrichResult { success: boolean; data?: Partial<Client>; error?: string; }

/** Company enrichment. REST → POST /clients/enrich. */
export async function enrichClientInfo(name: string, locale?: string): Promise<EnrichResult> {
  return apiPost<EnrichResult>('/clients/enrich', { name, locale });
}
export async function archiveClient(id: string, isArchived: boolean): Promise<void> {
  // Verbatim inverted semantics preserved from the source: archivedAt is set
  // to null when isArchived is true, timestamped otherwise.
  await apiPut(`/clients/${id}`, { archivedAt: isArchived ? null : Timestamp.now() });
}

// --- tags (TagsCatalogTab.tsx) ---
export async function createTag(label: string): Promise<void> {
  const payload = { label, isArchived: false };
  await apiPost('/tags', payload);
}
export async function setTagArchived(id: string, isArchived: boolean): Promise<void> {
  await apiPut(`/tags/${id}`, { isArchived });
}

// --- designationCatalog (DesignationCatalogTab.tsx) ---
export async function createDesignation(input: {
  name: string;
  section: CatalogSection | '';
  billingMode: BillingMode;
  variants: string[];
}): Promise<void> {
  const payload = {
    name: input.name, section: input.section, billingMode: input.billingMode,
    variants: input.variants, isArchived: false,
  };
  await apiPost('/designationCatalog', payload);
}
export async function updateDesignation(id: string, input: {
  name: string;
  billingMode: BillingMode;
  variants: string[];
}): Promise<void> {
  const patch = { name: input.name, billingMode: input.billingMode, variants: input.variants };
  await apiPut(`/designationCatalog/${id}`, patch);
}
export async function setDesignationArchived(id: string, isArchived: boolean): Promise<void> {
  await apiPut(`/designationCatalog/${id}`, { isArchived });
}

// --- users (UsersTab.tsx) ---
export async function setUserDepartment(id: string, departmentId: string): Promise<void> {
  await apiPut(`/users/${id}`, { departmentId });
}
export async function setUserJobTitle(id: string, jobTitle: string): Promise<void> {
  await apiPut(`/users/${id}`, { jobTitle });
}
export async function setUserActive(id: string, active: boolean): Promise<void> {
  await apiPut(`/users/${id}`, { active });
}
export async function setUserName(id: string, firstName: string, lastName: string): Promise<void> {
  // displayName is derived; compute it client-side so the stored value stays uniform.
  await apiPut(`/users/${id}`, { firstName, lastName, displayName: formatDisplayName(firstName, lastName) });
}

// Roles are assigned in Entra, never from the app — `PUT /users/:oid/roles` was
// removed 2026-09-18. See UsersTab.tsx for why, tools/users/reconcile-roles.ts
// for how the display mirror is refreshed.

// --- userInvites (UsersTab.tsx — « Ajouter un utilisateur ») ---
// L'e-mail est la seule clé de rapprochement avec le jeton Entra : normalisé
// ici ET revalidé par le serveur, qui refuse tout ce qui n'est pas déjà en
// minuscules (authz/validators.ts isValidUserInvite).
export async function createUserInvite(input: {
  email: string; firstName: string; lastName: string; departmentId: string;
}): Promise<void> {
  await apiPost('/userInvites', {
    email: input.email.trim().toLowerCase(),
    firstName: input.firstName.trim(),
    lastName: input.lastName.trim(),
    departmentId: input.departmentId,
  });
}
export async function deleteUserInvite(id: string): Promise<void> {
  await apiDelete(`/userInvites/${id}`);
}

// --- users prefs (ProjectsHub.tsx favourite/hide toggles) ---
// The server stamps updatedAt on these toggles (harmless). Firestore's
// arrayUnion/arrayRemove sentinels have no REST equivalent, so this reads the
// user doc, mutates the array, and writes it back.
// ponytail: read-modify-write, non-atomic — a single user toggling the same
// project from two tabs at once could clobber one toggle; negligible for a
// personal favourites list. Add a server array-op endpoint only if it bites.
async function toggleUserArrayField(
  uid: string,
  field: 'favorites' | 'hiddenProjects',
  value: string,
  add: boolean,
): Promise<void> {
  const user = await apiGet<Record<string, unknown>>(`/users/${uid}`);
  const current = new Set((user[field] as string[] | undefined) ?? []);
  if (add) current.add(value);
  else current.delete(value);
  await apiPut(`/users/${uid}`, { [field]: [...current] });
}
export async function setProjectFavourite(uid: string, projectId: string, isFavourite: boolean): Promise<void> {
  await toggleUserArrayField(uid, 'favorites', projectId, isFavourite);
}
export async function setProjectHidden(uid: string, projectId: string, isHidden: boolean): Promise<void> {
  await toggleUserArrayField(uid, 'hiddenProjects', projectId, isHidden);
}
